/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import MsDyn365, { IGridSettings, IImageData, Image, RichText, RichTextComponent } from '@msdyn365-commerce/core';

import * as React from 'react';
import { useInView } from 'react-intersection-observer';

import Badge from 'mfcl/Badge';
import Countdown, { CountdownRenderProps, zeroPad } from 'react-countdown';
import { fireAnalyticsLink } from '../../Utilities/analytics/clickTrack';
import { htmltoText } from '../../Utilities/analytics/htmlToText';
import { firePromotionClick, firePromotionImpression } from '../../Utilities/analytics/promotionClickTrack';
import { getAnalyticsConfig, getPromoAnalyticsConfig } from './analytics';
import { ITealiumEventData, ITealiumPromotionData } from './mfrm-content-reel.props.autogenerated';
import classname from 'classnames';

interface ISaleData {
    image: IImageData | undefined;
    iconImage: IImageData | undefined;
    carouselIndex: number;
    title: RichText;
    gridSettings: IGridSettings;
    description: RichText | undefined;
    promoBadge: string | undefined;
    countdown: Date | undefined;
    linkTo: string;
    salesTitle: boolean;
    enhancedEcommTracking: string;
    tealiumEvent: ITealiumEventData | undefined;
    tealiumPromotion: ITealiumPromotionData | undefined;
    cardShape: string;
    cardColor: string;
    titleColor: string;
    descriptionColor: string;
}

export default (props: ISaleData) => {
    const styles = 'mfrm-content-reel__';
    const {
        image,
        iconImage,
        title,
        gridSettings,
        description,
        promoBadge = '',
        countdown = '',
        linkTo,
        salesTitle,
        carouselIndex,
        tealiumEvent,
        tealiumPromotion,
        enhancedEcommTracking,
        cardShape,
        cardColor,
        titleColor,
        descriptionColor
    } = props;

    const promoName = htmltoText(title.toString()).trim();

    const analyticsConfig = getAnalyticsConfig(promoName, carouselIndex, tealiumEvent);
    const promoAnalyticsConfig = getPromoAnalyticsConfig(promoName, image, linkTo, carouselIndex, tealiumPromotion);

    const isPromotion = enhancedEcommTracking === 'promotion';

    const button = !image ? <div className={classname(`${styles}red-cta`)}>Start</div> : null;

    const handleAnalytics = () => {
        if (MsDyn365.isBrowser) {
            // @ts-ignore
            if ('utag' in window) {
                const pageURL = MsDyn365.isBrowser && window.location.pathname;
                // Note: Only Sleep Disruptors cards have square card shapes at the moment.
                if (cardShape === 'square' && button === null) {
                    // @ts-ignore
                    utag.link({
                        event: 'main-sleep-disruptor-nav-module',
                        event_category: 'main sleep disruptor nav module',
                        event_action: `${promoName} click - position ${carouselIndex + 1}`,
                        event_label: `sleep disruptor ${pageURL}`,
                        event_noninteraction: 'false'
                    });
                } else if (cardShape === 'square' && button !== null) {
                    // @ts-ignore
                    utag.link({
                        event: 'mattress-matcher-tile-inside-main-sleep-disruptor-nav-module',
                        event_category: 'main sleep disruptor nav module',
                        event_action: 'Mattress Matcher start button CTA click',
                        event_label: `sleep disruptor ${pageURL}`,
                        event_noninteraction: 'false'
                    });
                } else {
                    fireAnalyticsLink({ ...analyticsConfig });
                }
                if (isPromotion) {
                    firePromotionClick({ ...promoAnalyticsConfig });
                }
            }
        window.location.href = linkTo;
        }
    };

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();
        handleAnalytics();
    };

    const [ref, inView] = useInView({
        triggerOnce: true,
        rootMargin: '25px'
    });

    React.useEffect(() => {
        if (isPromotion && inView) {
            firePromotionImpression({ ...promoAnalyticsConfig });
        }
    }, [inView]);

    const [countdownRunning, setCountdownRunning] = React.useState(!!countdown);
    const renderer = ({ days, hours, minutes, seconds, completed }: CountdownRenderProps) => {
        if (completed) {
            setCountdownRunning(false);
            return <></>;
        } else {
            return (
                <span className={`countdown-${days ? 'lg' : 'sm'}`}>
                    {days ? `${zeroPad(days, 2)}:` : null}
                    {zeroPad(hours, 2)}:{zeroPad(minutes, 2)}:{zeroPad(seconds, 2)}
                </span>
            );
        }
    };

    const cardBkgColor = cardColor === 'white' ? `${styles}cardBkgColor-white` : '';
    const colorOfTitle = titleColor ? `${styles}text-${titleColor}` : '';
    const colorOfDescription = descriptionColor ? `${styles}text-${descriptionColor}` : '';
    const imageIcon =
        typeof iconImage !== 'undefined' ? (
            <div className={`${styles}icon-image`}>
                <Image {...iconImage} alt={iconImage?.altText} gridSettings={gridSettings} />
            </div>
        ) : null;

    const bkgImage =
        typeof image !== 'undefined' ? (
            <Image
                {...image}
                alt={image?.altText}
                className={classname(cardShape === 'square' ? `${styles}square-image` : `${styles}image`)}
                src={image?.src || ''}
                gridSettings={gridSettings}
            />
        ) : null;

    return (
        <a ref={ref} href={linkTo} className={`${styles}link`} onClick={handleClick}>
            <div
                className={classname(
                    cardBkgColor,
                    cardShape === 'square' && image ? `${styles}square` : '',
                    cardShape === 'square' && !image ? `${styles}square-no-image` : '',
                    cardShape !== 'square' ? `${styles}sale` : ''
                )}
            >
                {bkgImage}
                {imageIcon}
                <div
                    className={classname(
                        cardShape === 'square' && image ? `${styles}text-container-square` : '',
                        cardShape === 'square' && !image ? `${styles}text-container-square-no-image` : '',
                        cardShape !== 'square' ? `${styles}text-container` : ''
                    )}
                >
                    <div
                        className={classname(
                            cardShape === 'square' && image ? `${styles}title-square` : '',
                            cardShape === 'square' && !image ? `${styles}title-square-no-image` : '',
                            cardShape !== 'square' ? `${styles}title` : '',
                            salesTitle ? 'sales' : '',
                            colorOfTitle
                        )}
                    >
                        <RichTextComponent text={title} />
                    </div>
                    {description && (
                        <RichTextComponent
                            className={classname(
                                image ? `${styles}description-container` : `${styles}description-container-no-image`,
                                colorOfDescription
                            )}
                            text={description}
                        />
                    )}
                    {button}
                </div>
                {promoBadge && <Badge className={`${styles}badge-container ${styles}promo`}>{promoBadge}</Badge>}
                {countdown && countdownRunning && (
                    <Badge className={`${styles}badge-container ${styles}countdown`} type='secondary'>
                        <Countdown date={countdown} renderer={renderer} />
                    </Badge>
                )}
            </div>
        </a>
    );
};
